/**
 * Cross-Sell Drawer
 *
 * The "Added to Cart" drawer that opens automatically after a product is
 * added to the cart. Shows the added product, the cart contents (accordion
 * on mobile, side panel on desktop), recommendations with quick-add, the
 * subtotal and the checkout CTAs.
 *
 * The classic cart drawer (drawer.js) stays untouched and keeps its header
 * cart-icon trigger; only one of the two drawers is ever open.
 */

import { addToCart, getCrossSellDrawerContent, getProductQuickView } from './api'
import { hideDrawer } from './drawer'
import { OverlayScrollbars } from 'overlayscrollbars'

let scrollInstance = null

/** How long the "Added" confirmation stays on a button before it resets. */
const ADDED_CONFIRMATION_MS = 1600

/** Pending "Added" resets, so a second add restarts rather than stacks. */
const addedTimers = new WeakMap()
let currentProductId = null

/** Set when a quick-add changed the cart while the contents panel was shut. */
let cartPanelStale = false

/**
 * Initialize cross-sell drawer event handlers (all delegated on document)
 */
export function initializeCrossSellDrawerEvents() {
  // Close button
  document.addEventListener('click', (e) => {
    if (e.target.matches('#td-cs-drawer-close, #td-cs-drawer-close *')) {
      hideCrossSellDrawer()
    }
  })

  // Shadow click to close
  document.addEventListener('click', (e) => {
    if (e.target.matches('.td-cs-drawer-shadow')) {
      hideCrossSellDrawer()
    }
  })

  // "Continue Shopping" closes the drawer
  document.addEventListener('click', (e) => {
    if (e.target.matches('#td-cs-drawer-continue, #td-cs-drawer-continue *')) {
      hideCrossSellDrawer()
    }
  })

  // Escape closes the topmost layer: the quick view before the drawer
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return

    if (document.querySelector('.td-cs-quickview.active')) {
      hideQuickView()
      return
    }

    if (document.querySelector('.td-cs-drawer.active')) {
      hideCrossSellDrawer()
    }
  })

  // Cart contents toggle (accordion on mobile, side panel on desktop)
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('.td-cs-drawer__cart-toggle')
    if (!toggle) return

    const cartSection = toggle.closest('.td-cs-drawer__cart')
    if (!cartSection) return

    const isOpen = cartSection.classList.toggle('open')
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false')

    // The line items were left behind by a quick-add; fetch them now that
    // they are about to be looked at.
    if (isOpen && cartPanelStale) {
      refreshCartRegions()
    }
  })

  // Quick-add of a recommended product, from the row or the quick view
  document.addEventListener('click', (e) => {
    const button = e.target.closest('.td-cs-drawer .cs-add-button, .td-cs-quickview .cs-add-button')
    if (button) {
      handleRecommendedAdd(button)
    }
  })

  // "View Details" opens the quick view
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('.cs-view-details')
    if (trigger) {
      showQuickView(trigger.dataset.productId, trigger.dataset.position)
    }
  })

  // Quick view: close button, shadow click
  document.addEventListener('click', (e) => {
    if (e.target.closest('#td-cs-quickview-close') || e.target.matches('.td-cs-quickview-shadow')) {
      hideQuickView()
    }
  })

  // Quick view: thumbnails swap the main image
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('.td-cs-quickview__thumb')
    if (thumb) {
      selectQuickViewImage(thumb)
    }
  })

  // The header cart icon opens the classic drawer — close this one first,
  // leaving the body scroll lock in place for the drawer taking over.
  document.addEventListener('click', (e) => {
    if (e.target.matches('.site-header__tools--cart--button, .site-header__tools--cart--button *, .site-header__cart--button, .site-header__cart--button *')) {
      hideCrossSellDrawer(false)
    }
  })
}

/**
 * Show the cross-sell drawer for a just-added product
 * @param {number|string} productId
 */
export async function showCrossSellDrawer(productId) {
  const { drawer, drawerShadow } = getDrawerElements()

  // Shell missing (e.g. stripped-down template) — classic drawer fallback
  if (!drawer || !drawerShadow) {
    const { showDrawer } = await import('./drawer')
    return showDrawer()
  }

  currentProductId = parseInt(productId, 10) || null

  // Only one drawer at a time
  if (document.querySelector('.td-drawer.active')) {
    hideDrawer()
  }

  document.body.classList.add('noscroll')
  document.body.style.overflow = 'hidden'
  drawer.classList.add('active')
  drawerShadow.classList.add('active')

  await updateCrossSellDrawer()
}

/**
 * Hide the cross-sell drawer
 * @param {boolean} restoreScroll false when another drawer takes over the lock
 */
export function hideCrossSellDrawer(restoreScroll = true) {
  const { drawer, drawerShadow } = getDrawerElements()

  // The quick view sits on top of the drawer — never leave it orphaned.
  hideQuickView()

  if (drawer && drawerShadow && drawer.classList.contains('active')) {
    drawer.classList.remove('active')
    drawerShadow.classList.remove('active')

    if (restoreScroll) {
      document.body.classList.remove('noscroll')
      document.body.style.overflow = 'auto'
    }
  }

  if (scrollInstance) {
    scrollInstance.destroy()
    scrollInstance = null
  }
}

/**
 * Fetch and render the drawer content for the current product context
 */
async function updateCrossSellDrawer() {
  const content = document.querySelector('#td-cs-drawer-content')
  const loading = document.querySelector('.td-cs-drawer .loading')

  if (!content) return

  setLoadingState(loading, true)
  cartPanelStale = false

  try {
    const data = await getCrossSellDrawerContent(currentProductId)

    content.innerHTML = data.html
    updateCartIcon(data)

    requestAnimationFrame(() => {
      initializeScrollArea()
    })
  } catch (error) {
    console.error('Error updating cross-sell drawer:', error)
  } finally {
    setLoadingState(loading, false)
  }
}

/**
 * Re-render only what the cart drives — the contents panel, the subtotal and
 * the header cart icon — leaving the recommendations list exactly as it is.
 *
 * Quick-add used to re-render the whole drawer, which moved the remaining
 * rows out from under the pointer and made the just-added product vanish.
 * The row now stays put and its button reports the outcome instead.
 */
async function refreshCartRegions() {
  const content = document.querySelector('#td-cs-drawer-content')

  if (!content) return

  cartPanelStale = false

  const data = await getCrossSellDrawerContent(currentProductId)

  const fresh = document.createElement('div')
  fresh.innerHTML = data.html

  const wasOpen = !!content.querySelector('.td-cs-drawer__cart.open')

  for (const selector of [ '.td-cs-drawer__cart', '.td-cs-drawer__subtotal' ]) {
    const current = content.querySelector(selector)
    const replacement = fresh.querySelector(selector)

    if (current && replacement) {
      current.replaceWith(replacement)
    }
  }

  if (wasOpen) {
    const cartSection = content.querySelector('.td-cs-drawer__cart')
    cartSection?.classList.add('open')
    cartSection?.querySelector('.td-cs-drawer__cart-toggle')?.setAttribute('aria-expanded', 'true')
  }

  syncInCartNotes(content, fresh)
  updateCartIcon(data)
}

/**
 * Update everything the drawer says about the cart straight from the Store
 * API response of an add — no second request to the server.
 *
 * Staging spends ~2s in WordPress bootstrap on every request, so re-rendering
 * the whole drawer just to move a subtotal was the slowest thing in the
 * interaction. The response already carries the counts, the totals and the
 * quantities; only the contents panel needs server-rendered markup, and that
 * is fetched when it is opened.
 *
 * @param {object} cart Store API cart payload
 * @param {number|string} productId the product that was just added
 * @returns {boolean} false when the caller still has to re-render
 */
function applyCartResponse(cart, productId) {
  const totals = cart?.totals

  if (!totals || typeof cart.items_count !== 'number') return false

  const content = document.querySelector('#td-cs-drawer-content')
  if (!content) return false

  // The line items are markup only the server renders — if they are on
  // screen, the caller has to re-render after all.
  if (content.querySelector('.td-cs-drawer__cart.open')) return false

  const subtotal = formatStoreApiPrice(totals.total_items, totals)
  if (subtotal === null) return false

  for (const count of content.querySelectorAll('.td-cs-drawer__cart-count')) {
    count.textContent = String(cart.items_count)
  }

  const price = content.querySelector('.td-cs-drawer__subtotal-price')
  if (price) price.textContent = subtotal

  const added = (cart.items || []).find((item) => String(item.id) === String(productId))
  updateInCartNote(productId, added?.quantity ?? 0)
  updateCartIconCount(cart.items_count)

  cartPanelStale = true

  return true
}

/**
 * Render a Store API money value the way WooCommerce would, using only the
 * currency settings the response itself carries.
 */
function formatStoreApiPrice(value, totals) {
  const minor = Number(totals.currency_minor_unit)
  const amount = Number(value)

  if (!Number.isFinite(amount) || !Number.isFinite(minor)) return null

  const [whole, fraction] = (amount / 10 ** minor).toFixed(minor).split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, totals.currency_thousand_separator ?? ',')
  const number = fraction ? grouped + (totals.currency_decimal_separator ?? '.') + fraction : grouped

  return `${totals.currency_prefix ?? ''}${number}${totals.currency_suffix ?? ''}`
}

/**
 * Put the "In cart: n" note on a recommendation row (and its quick view),
 * adding or removing it as the quantity requires.
 */
function updateInCartNote(productId, quantity) {
  const row = document.querySelector(
    `.td-cs-drawer__rec-item[data-product-id="${CSS.escape(String(productId))}"]`
  )
  if (!row) return

  const label = row.closest('.td-cs-drawer__recommended-list')?.dataset.inCartLabel
  const info = row.querySelector('.td-cs-drawer__rec-info')
  let note = row.querySelector('.td-cs-drawer__rec-in-cart')

  if (!quantity || !label || !info) {
    note?.remove()
    return
  }

  if (!note) {
    note = document.createElement('p')
    note.className = 'td-cs-drawer__rec-in-cart'
    info.append(note)
  }

  note.textContent = label.replace('%d', String(quantity))
}

/**
 * Keep the header cart icon's badge in step, creating it when the cart was
 * empty and the badge had not been rendered at all.
 */
function updateCartIconCount(count) {
  const icon = document.querySelector(
    '.site-header__tools--cart--button .icon, .site-header__cart--button .icon'
  )
  if (!icon) return

  let badge = icon.querySelector('.sum')

  if (!count) {
    badge?.remove()
    return
  }

  if (!badge) {
    badge = document.createElement('span')
    badge.className = 'sum'
    icon.append(badge)
  }

  badge.textContent = String(count)
}

/**
 * Carry the "In cart: n" note of every recommendation over from a freshly
 * rendered drawer, without touching the rows themselves — they must stay put
 * under the pointer while the customer works through the list.
 */
function syncInCartNotes(content, fresh) {
  for (const row of content.querySelectorAll('.td-cs-drawer__rec-item')) {
    const id = row.dataset.productId
    if (!id) continue

    const current = row.querySelector('.td-cs-drawer__rec-in-cart')
    const replacement = fresh
      .querySelector(`.td-cs-drawer__rec-item[data-product-id="${CSS.escape(id)}"] .td-cs-drawer__rec-in-cart`)

    if (current && replacement) {
      current.replaceWith(replacement)
    } else if (current) {
      current.remove()
    } else if (replacement) {
      row.querySelector('.td-cs-drawer__rec-info')?.append(replacement)
    }
  }
}

/**
 * Update the header cart icon from a drawer payload
 */
function updateCartIcon(data) {
  const cartIconContainer = document.querySelector('.site-header__tools--cart, .site-header__cart')

  if (cartIconContainer && data.cart_icon_html) {
    cartIconContainer.innerHTML = data.cart_icon_html
  }
}

/**
 * Quick-add a recommended product, attributed to the cross-sell drawer
 */
async function handleRecommendedAdd(button) {
  const productId = button.dataset.productId
  if (!productId) return

  button.classList.add('is-loading')
  button.disabled = true

  try {
    const cart = await addToCart(productId, 1, {
      source: 'cross_sell_drawer',
      origin: currentProductId,
      position: button.dataset.position
    })

    // The row stays where it is; the button reports the outcome so several
    // add-ons can be picked without the list shifting underneath.
    markAdded(button)

    // An add made from the quick view has to leave the same trace on the row
    // behind it, otherwise closing the modal shows an untouched "Add to cart".
    if (button.closest('.td-cs-quickview')) {
      const row = document.querySelector(
        `.td-cs-drawer .cs-add-button[data-product-id="${CSS.escape(productId)}"]`
      )
      if (row) markAdded(row)

      hideQuickView()
    }

    // Everything the drawer shows about the cart — the counts, the subtotal
    // and this row's "In cart" note — is in the response we already have, so
    // the second round trip only happens when the contents panel is open and
    // its line items are actually on screen.
    if (!applyCartResponse(cart, productId)) {
      await refreshCartRegions()
    }
  } catch (error) {
    console.error('Error adding recommended product to cart:', error)
    button.classList.remove('is-loading')
    button.disabled = false
  }
}

/**
 * Confirm an add on the button itself, then hand the button back.
 *
 * The confirmation is deliberately temporary: add-ons are bought two at a
 * time more often than not (a spare battery, a second coil), so a row that
 * locks itself after one click is a dead end. The quantity already in the
 * cart is reported next to the price instead — see refreshCartRegions().
 */
function markAdded(button) {
  button.classList.remove('is-loading')

  if (!button.dataset.addLabel) {
    button.dataset.addLabel = button.textContent.trim()
  }

  button.classList.add('is-added')
  button.disabled = true

  if (button.dataset.addedLabel) {
    button.textContent = button.dataset.addedLabel
  }

  window.clearTimeout(addedTimers.get(button))
  addedTimers.set(
    button,
    window.setTimeout(() => {
      button.classList.remove('is-added')
      button.disabled = false
      button.textContent = button.dataset.addLabel
      addedTimers.delete(button)
    }, ADDED_CONFIRMATION_MS)
  )
}

/**
 * Open the quick view for a recommended product. The drawer stays open
 * underneath — the modal sits above it and hands control straight back.
 * @param {number|string} productId
 * @param {number|string} position position in the recommendations list
 */
async function showQuickView(productId, position = 0) {
  const modal = document.querySelector('.td-cs-quickview')
  const shadow = document.querySelector('.td-cs-quickview-shadow')
  const content = document.querySelector('#td-cs-quickview-content')
  const loading = document.querySelector('.td-cs-quickview .loading')

  if (!modal || !shadow || !content) return

  modal.classList.add('active')
  modal.setAttribute('aria-hidden', 'false')
  shadow.classList.add('active')
  setLoadingState(loading, true)

  try {
    const data = await getProductQuickView(productId, position)
    content.innerHTML = data.html
    content.querySelector('#td-cs-quickview-close')?.focus()
  } catch (error) {
    console.error('Error loading product quick view:', error)
    hideQuickView()
  } finally {
    setLoadingState(loading, false)
  }
}

/**
 * Close the quick view. The body scroll lock belongs to the drawer, which is
 * still open behind it, so it is left alone.
 */
function hideQuickView() {
  const modal = document.querySelector('.td-cs-quickview')
  const shadow = document.querySelector('.td-cs-quickview-shadow')
  const content = document.querySelector('#td-cs-quickview-content')

  modal?.classList.remove('active')
  modal?.setAttribute('aria-hidden', 'true')
  shadow?.classList.remove('active')

  if (content) {
    content.innerHTML = ''
  }
}

/**
 * Swap the quick view's main image for the clicked thumbnail
 */
function selectQuickViewImage(thumb) {
  const main = document.querySelector('#td-cs-quickview-main-image')

  if (!main || !thumb.dataset.imageSrc) return

  main.src = thumb.dataset.imageSrc
  main.srcset = thumb.dataset.imageSrcset || ''

  thumb.closest('.td-cs-quickview__gallery-thumbs')
    ?.querySelectorAll('.td-cs-quickview__thumb')
    .forEach((el) => el.classList.toggle('is-active', el === thumb))
}

/**
 * Get drawer DOM elements
 */
function getDrawerElements() {
  return {
    drawer: document.querySelector('.td-cs-drawer'),
    drawerShadow: document.querySelector('.td-cs-drawer-shadow')
  }
}

/**
 * Set loading state
 */
function setLoadingState(loadingElement, isLoading) {
  if (!loadingElement) return

  if (isLoading) {
    loadingElement.classList.add('active')
  } else {
    loadingElement.classList.remove('active')
  }
}

/**
 * Initialize OverlayScrollbars on the recommendations region. The region is
 * flex-sized (see cross-sell-drawer.scss), so no height measuring is needed.
 */
function initializeScrollArea() {
  const scrollArea = document.querySelector('#td-cs-drawer-content .scroll')

  if (!scrollArea) return

  if (scrollInstance) {
    scrollInstance.destroy()
    scrollInstance = null
  }

  scrollInstance = OverlayScrollbars(scrollArea, {
    scrollbars: {
      autoHide: 'never',
    },
    overflow: {
      x: 'hidden',
      y: 'scroll'
    }
  })
}
